/**
 * feat-305 — the assistant's small, safe markdown renderer (ADR 0001 §10.4).
 *
 * Deliberately NOT react-markdown and NOT readingMarkdown.tsx (whose dialect is
 * frozen to match Flutter). It tokenizes into React nodes and never uses
 * `dangerouslySetInnerHTML`, so any HTML in a message is inert text.
 *
 * Dialect:
 *   blocks  — paragraphs (blank-line separated); `- ` / `* ` and `1. ` lists;
 *             `#`–`###` headings and a line that is only `**…**` render as a
 *             bold paragraph (the model is told not to use headings).
 *   inline  — `**bold**`, `*italic*`, and footnote refs `[^n]`. Adjacent refs
 *             (`[^2][^3]`) render as ONE marker "2,3" (two sups read as "23").
 *             A ref renders as a marker ONLY when `n` is in the message's
 *             footnotes; otherwise it stays literal text.
 *
 * PRIVACY (spec §5, ADR §4.4): no links and no images, ever. `[label](url)`
 * renders the label as plain text; `![alt](url)` renders the alt text. Nothing
 * this renders is an `<a>`, an `<img>`, focusable, or has a link role.
 *
 * Streaming: an unclosed `**` or `*` renders literally until its closer arrives
 * (no half-parsed structure).
 */
import React from 'react';
import type { Footnote } from './types';
import { ASSISTANT_COPY } from './copy';

type Inline = React.ReactNode;

const FOOTNOTE_RUN = /^(?:\[\^\d+\])+/;
const IMAGE = /^!\[([^\]\n]*)\]\(([^)\n]*)\)/;
const LINK = /^\[([^\]\n]*)\]\(([^)\n]*)\)/;

function FootnoteMarker({ notes }: { notes: Footnote[] }) {
  const ns = notes.map((f) => f.n).join(',');
  return (
    <sup
      className="ml-px text-[11px] font-bold text-inkMuted cursor-default select-none"
      data-footnote-ref={ns}
    >
      <span className="sr-only">{notes.length > 1 ? 'shifts ' : 'shift '}</span>
      {ns}
    </sup>
  );
}

/** Parse inline markdown into React nodes. Exported for tests. */
export function renderInline(src: string, footnotes: Footnote[], keyBase = 'i'): Inline[] {
  const out: Inline[] = [];
  let text = '';
  let k = 0;
  const key = () => `${keyBase}-${(k += 1)}`;
  const flush = () => {
    if (text) {
      out.push(text);
      text = '';
    }
  };
  const byN = new Map(footnotes.map((f) => [f.n, f]));

  let i = 0;
  while (i < src.length) {
    const rest = src.slice(i);
    const ch = src[i];

    if (ch === '[') {
      const run = FOOTNOTE_RUN.exec(rest);
      if (run) {
        const refs = Array.from(run[0].matchAll(/\[\^(\d+)\]/g)).map((m) => Number(m[1]));
        // Keep known refs together as one marker; unknown refs stay literal.
        let group: Footnote[] = [];
        const flushGroup = () => {
          if (group.length) {
            flush();
            out.push(<FootnoteMarker key={key()} notes={group} />);
            group = [];
          }
        };
        for (const n of refs) {
          const f = byN.get(n);
          if (f) {
            if (!group.some((g) => g.n === n)) group.push(f);
          } else {
            flushGroup();
            text += `[^${n}]`;
          }
        }
        flushGroup();
        i += run[0].length;
        continue;
      }
      const link = LINK.exec(rest);
      if (link) {
        // Privacy: a link renders as its label, never as an anchor.
        flush();
        out.push(...renderInline(link[1], footnotes, key()));
        i += link[0].length;
        continue;
      }
    }

    if (ch === '!' && src[i + 1] === '[') {
      const img = IMAGE.exec(rest);
      if (img) {
        // Privacy: an image renders as its alt text, never as an <img>.
        text += img[1];
        i += img[0].length;
        continue;
      }
    }

    if (ch === '*' && src[i + 1] === '*') {
      const close = src.indexOf('**', i + 2);
      if (close > i + 2) {
        flush();
        out.push(
          <strong key={key()} className="font-bold">
            {renderInline(src.slice(i + 2, close), footnotes, key())}
          </strong>,
        );
        i = close + 2;
        continue;
      }
      // Unclosed (still streaming): literal.
      text += '**';
      i += 2;
      continue;
    }

    if (ch === '*' && src[i + 1] !== ' ' && i + 1 < src.length) {
      // Italic: a single '*' closed by a single '*' that isn't part of '**'.
      let close = -1;
      for (let j = i + 1; j < src.length; j++) {
        if (src[j] === '*') {
          if (src[j + 1] === '*') {
            j++;
            continue;
          }
          if (src[j - 1] !== ' ') close = j;
          break;
        }
      }
      if (close > i + 1) {
        flush();
        out.push(<em key={key()}>{renderInline(src.slice(i + 1, close), footnotes, key())}</em>);
        i = close + 1;
        continue;
      }
    }

    text += ch;
    i += 1;
  }
  flush();
  return out;
}

type SubList = { kind: 'ul' | 'ol'; items: string[] };
export type ListItem = { text: string; children: SubList[] };
type Block =
  | { kind: 'p'; text: string; bold?: boolean }
  | { kind: 'ul' | 'ol'; items: ListItem[]; start?: number };

const UL_ITEM = /^(\s*)[-*]\s+(.*)$/;
const OL_ITEM = /^(\s*)(\d+)[.)]\s+(.*)$/;
const HEADING = /^#{1,3}\s+(.*)$/;
const BOLD_LINE = /^\*\*([^*]+)\*\*$/;

const indentOf = (ws: string) => ws.replace(/\t/g, '    ').length;

/**
 * Lists nest one level. A list line nests under the previous item when it is
 * indented (2+ spaces), or when it is a bullet on the line directly after a
 * numbered item (no blank line between):
 * the model often writes "1. Point" then unindented "- detail" lines, and
 * meaning a sub-list is the only sensible reading. Without this, every bullet
 * ended the numbered list and the next number restarted at 1. An ordered list
 * also keeps the number it starts at, so a list split by a paragraph still
 * continues 3, 4, … instead of restarting.
 */
export function parseBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  let para: string[] = [];
  let afterBlank = false;
  const flushPara = () => {
    if (para.length) {
      blocks.push({ kind: 'p', text: para.join(' ') });
      para = [];
    }
  };
  const lines = body.replace(/\r\n?/g, '\n').split('\n');
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushPara();
      afterBlank = true;
      continue;
    }
    const blankBefore = afterBlank;
    afterBlank = false;
    const ul = UL_ITEM.exec(line);
    const ol = ul ? null : OL_ITEM.exec(line);
    if (ul || ol) {
      flushPara();
      const kind = ul ? 'ul' : 'ol';
      const indent = indentOf((ul || ol)![1]);
      const item = ul ? ul[2] : ol![3];
      const last = blocks[blocks.length - 1];
      const lastList = last && last.kind !== 'p' ? last : null;
      const nest = !!lastList && lastList.items.length > 0 && (indent >= 2 || (kind === 'ul' && lastList.kind === 'ol' && !blankBefore));
      if (nest) {
        const parent = lastList!.items[lastList!.items.length - 1];
        const sub = parent.children[parent.children.length - 1];
        if (sub && sub.kind === kind) sub.items.push(item);
        else parent.children.push({ kind, items: [item] });
      } else if (lastList && lastList.kind === kind) {
        lastList.items.push({ text: item, children: [] });
      } else {
        const start = ol ? Number(ol[2]) : undefined;
        blocks.push({ kind, items: [{ text: item, children: [] }], ...(start && start !== 1 ? { start } : {}) });
      }
      continue;
    }
    const h = HEADING.exec(line.trim());
    if (h) {
      flushPara();
      blocks.push({ kind: 'p', text: h[1], bold: true });
      continue;
    }
    const b = BOLD_LINE.exec(line.trim());
    if (b && para.length === 0) {
      blocks.push({ kind: 'p', text: b[1], bold: true });
      continue;
    }
    // A continuation line directly under a list item belongs to that item
    // (or to its last sub-item).
    const last = blocks[blocks.length - 1];
    if (para.length === 0 && last && last.kind !== 'p' && /^\s+/.test(raw)) {
      const item = last.items[last.items.length - 1];
      const sub = item.children[item.children.length - 1];
      if (sub) sub.items[sub.items.length - 1] += ` ${line.trim()}`;
      else item.text += ` ${line.trim()}`;
      continue;
    }
    para.push(line.trim());
  }
  flushPara();
  return blocks;
}

export interface AssistantMarkdownProps {
  body: string;
  footnotes: Footnote[];
  /** Rendered inline at the very end of the last block (the streaming caret). */
  trailing?: React.ReactNode;
}

export const AssistantMarkdown: React.FC<AssistantMarkdownProps> = ({ body, footnotes, trailing }) => {
  const blocks = parseBlocks(body);
  if (blocks.length === 0) {
    return trailing ? <p>{trailing}</p> : null;
  }
  const lastIdx = blocks.length - 1;
  return (
    <div className="space-y-2.5">
      {blocks.map((b, bi) => {
        const tail = bi === lastIdx ? trailing : null;
        if (b.kind === 'p') {
          const inner = renderInline(b.text, footnotes, `b${bi}`);
          return (
            <p key={bi}>
              {b.bold ? <strong className="font-bold">{inner}</strong> : inner}
              {tail}
            </p>
          );
        }
        const ListTag = b.kind;
        return (
          <ListTag
            key={bi}
            start={b.kind === 'ol' ? b.start : undefined}
            className={`${b.kind === 'ul' ? 'list-disc' : 'list-decimal'} pl-5 space-y-1.5 marker:text-inkMuted`}
          >
            {b.items.map((item, ii) => {
              const lastItem = ii === b.items.length - 1;
              const lastSub = item.children.length - 1;
              return (
                <li key={ii}>
                  {renderInline(item.text, footnotes, `b${bi}-${ii}`)}
                  {lastItem && lastSub < 0 ? tail : null}
                  {item.children.map((sub, si) => {
                    const SubTag = sub.kind;
                    return (
                      <SubTag
                        key={si}
                        className={`${sub.kind === 'ul' ? 'list-disc' : 'list-decimal'} mt-1.5 pl-5 space-y-1 marker:text-inkMuted`}
                      >
                        {sub.items.map((t, ti) => (
                          <li key={ti}>
                            {renderInline(t, footnotes, `b${bi}-${ii}-${si}-${ti}`)}
                            {lastItem && si === lastSub && ti === sub.items.length - 1 ? tail : null}
                          </li>
                        ))}
                      </SubTag>
                    );
                  })}
                </li>
              );
            })}
          </ListTag>
        );
      })}
    </div>
  );
};

/**
 * The "Shifts cited" list: plain text, numbered, never links (ADR §10.4,
 * NOTES §3). Ordered by `n` (the backend numbers by first appearance). A
 * footnote the text never references is still listed.
 */
export const FootnoteList: React.FC<{ footnotes: Footnote[] }> = ({ footnotes }) => {
  if (!footnotes.length) return null;
  const sorted = [...footnotes].sort((a, b) => a.n - b.n);
  return (
    <div className="mt-3 pt-2.5 border-t border-softBorder">
      <p className="text-xs font-semibold text-inkMuted">{ASSISTANT_COPY.shiftsCited}</p>
      <ol className="mt-1 space-y-0.5 text-xs text-inkMuted">
        {sorted.map((f) => (
          <li key={f.n} className="flex gap-2" data-testid={`assistant-footnote-${f.n}`}>
            <span className="w-3 shrink-0 text-right font-bold">{f.n}</span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{f.label}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

export interface PlainTextOptions {
  /**
   * `spoken` (default, the live region): markers read as " (shift 1)".
   * `bracket` (copy conversation): markers render as "[1]" / "[2][3]".
   */
  markers?: 'spoken' | 'bracket';
  /**
   * Keep line structure (one line per paragraph / list item, list items as
   * "- "). Default false: everything collapses to a single line.
   */
  keepLines?: boolean;
}

/**
 * Plain text for the screen-reader live region: markdown stripped, markers
 * read as "shift 1" (NOTES §6), links/images reduced to their text. The copy
 * conversation control reuses it with `{ markers: 'bracket', keepLines: true }`.
 */
export function markdownToPlainText(body: string, footnotes: Footnote[], opts: PlainTextOptions = {}): string {
  const known = new Set(footnotes.map((f) => f.n));
  const bracket = opts.markers === 'bracket';
  const inline = (s: string) =>
    s
      .replace(/!\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
      .replace(/(?:\[\^\d+\])+/g, (run) => {
        const ns = Array.from(run.matchAll(/\[\^(\d+)\]/g))
          .map((m) => Number(m[1]))
          .filter((n) => known.has(n));
        if (!ns.length) return run;
        if (bracket) return ns.map((n) => `[${n}]`).join('');
        return ` (${ns.length > 1 ? 'shifts' : 'shift'} ${ns.join(', ')})`;
      })
      .replace(/\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1$2')
      .replace(/[ \t]+/g, ' ')
      .trim();
  const normalized = body.replace(/\r\n?/g, '\n');
  if (!opts.keepLines) {
    return inline(
      normalized
        .replace(/^\s*#{1,3}\s+/gm, '')
        .replace(/^\s*[-*]\s+/gm, '')
        .replace(/\s+/g, ' '),
    );
  }
  // keepLines (Copy conversation): one line per paragraph or list item, built
  // from the SAME parse the renderer uses, so the copy nests and numbers exactly
  // as the screen shows. Ordered items carry their rendered numbers.
  const lines: string[] = [];
  for (const b of parseBlocks(normalized)) {
    if (b.kind === 'p') {
      const t = inline(b.text);
      if (t) lines.push(t);
      continue;
    }
    b.items.forEach((item, i) => {
      const marker = b.kind === 'ol' ? `${(b.start ?? 1) + i}.` : '-';
      lines.push(`${marker} ${inline(item.text)}`);
      for (const sub of item.children) {
        sub.items.forEach((t, j) => lines.push(`   ${sub.kind === 'ol' ? `${j + 1}.` : '-'} ${inline(t)}`));
      }
    });
  }
  return lines.join('\n');
}

/**
 * A STOPPED answer never gets its closing `**`, so the literal asterisks would
 * stay forever. Drop a dangling (unpaired, last) `**` and end the text with
 * "…" (NOTES §3: the partial text ends in "…"). Streaming text is untouched.
 */
export function finalizeStoppedBody(body: string): string {
  let text = body.trimEnd();
  const pairs = text.split('**').length - 1;
  if (pairs % 2 === 1) {
    const i = text.lastIndexOf('**');
    text = text.slice(0, i) + text.slice(i + 2);
  }
  text = text.trimEnd();
  if (!text) return text;
  // A cut-off answer gets an ellipsis; one that already ends a sentence doesn't.
  return /[….!?]$/.test(text) ? text : `${text}…`;
}
